/**
 * The generic, domain-agnostic path: the user maps columns of a `ParsedDataset` to a chart, and this
 * produces a `WidgetSpec`. Used whenever no domain preset claims the data (see `resolvePreset`), so any
 * spreadsheet — not just the Cisco inventory — can become a widget.
 *
 * Aggregation groups rows by the label column and rolls up the value column (`sum`/`avg`/`count`), so
 * a raw export with one row per record becomes a per-category bar/pie without the user pre-pivoting it.
 */
import { ParsedDataset } from '../parseDataset';
import { WidgetChartType, WidgetSpec } from './types';

export type GenericAggregate = 'sum' | 'count' | 'avg';

export interface GenericWidgetMapping {
  title: string;
  chartType: WidgetChartType;
  /** Column whose distinct values become the categories/labels. */
  labelColumn: string;
  /** Column rolled up per label. Ignored (and optional) when `aggregate` is `count`. */
  valueColumn?: string;
  aggregate: GenericAggregate;
  /** Keep only the top-N labels by value (after aggregation); unset keeps all. */
  limit?: number;
}

const toNumber = (v: any): number => {
  if (typeof v === 'number') return v;
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(String(v).replace(/[^0-9.eE+-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/** Field names emitted in every generic widget's rows, so tile settings can point at them uniformly. */
export const GENERIC_LABEL_FIELD = 'label';
export const GENERIC_VALUE_FIELD = 'value';

export function buildGenericWidget(
  dataset: ParsedDataset,
  mapping: GenericWidgetMapping
): WidgetSpec {
  const { labelColumn, valueColumn, aggregate, chartType, title, limit } = mapping;

  const groups = new Map<string, { sum: number; count: number }>();
  for (const row of dataset.rows) {
    const rawLabel = row[labelColumn];
    if (rawLabel === null || rawLabel === undefined || String(rawLabel).trim() === '') continue;
    const label = String(rawLabel).trim();
    const bucket = groups.get(label) || { sum: 0, count: 0 };
    bucket.count += 1;
    if (valueColumn) bucket.sum += toNumber(row[valueColumn]);
    groups.set(label, bucket);
  }

  let data = Array.from(groups.entries()).map(([label, { sum, count }]) => ({
    [GENERIC_LABEL_FIELD]: label,
    [GENERIC_VALUE_FIELD]:
      aggregate === 'count' ? count : aggregate === 'avg' ? (count ? sum / count : 0) : sum,
  }));

  data.sort((a, b) => (b[GENERIC_VALUE_FIELD] as number) - (a[GENERIC_VALUE_FIELD] as number));
  if (limit && limit > 0) data = data.slice(0, limit);

  return {
    id: `generic-${labelColumn}-${valueColumn || 'count'}-${chartType}`,
    title,
    chartType,
    data,
    settings: {
      labelField: GENERIC_LABEL_FIELD,
      valueField: GENERIC_VALUE_FIELD,
    },
    note:
      aggregate === 'count'
        ? `Count of rows by ${labelColumn}`
        : `${aggregate === 'avg' ? 'Average' : 'Sum'} of ${valueColumn} by ${labelColumn}`,
  };
}

/** A datagrid widget of the raw parsed rows — always offered so users can sanity-check the import. */
export function buildRawTableWidget(dataset: ParsedDataset): WidgetSpec {
  return {
    id: 'raw-table',
    title: `${dataset.sourceMeta.fileName} — all rows`,
    chartType: 'datagrid',
    data: dataset.rows,
    settings: { selectedColumns: dataset.columns },
    note: `${dataset.rowCount} rows × ${dataset.columns.length} columns`,
  };
}
